import { ExternalPageHeader } from "@/components/layout/external-page-header"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/features/authentication/components/auth-provider"
import { ApplicationProgressSidebar } from "@/features/membership/application/components/application-progress-sidebar"
import {
  useIsSavingMembershipApplicationDraft,
  useMembershipApplicationDraft,
} from "@/features/membership/application/api/useMembershipApplicationDraft"
import { useApplicationCategoryConfiguration } from "@/features/membership/application/hooks/useApplicationConfiguration"
import { Link, Outlet, createFileRoute } from "@tanstack/react-router"
import { formatDistanceToNow } from "date-fns"
import "@/features/membership/application/application.css"

export const Route = createFileRoute("/membership/apply")({
  component: MembershipApplicationLayout,
})

function MembershipApplicationLayout() {
  const auth = useAuth()
  const { data: draft, isLoading } = useMembershipApplicationDraft()
  const isSaving = useIsSavingMembershipApplicationDraft()
  const completedStepIds = draft?.completedStepIds ?? []
  const categoryId = draft?.category?.categoryId ?? null
  const { visibleSteps, configuration, category } = useApplicationCategoryConfiguration(categoryId)

  const autosaveLabel = (() => {
    if (isSaving) {
      return "Saving draft..."
    }
    if (isLoading) {
      return "Loading draft details..."
    }
    if (draft?.lastSavedAt) {
      const parsed = new Date(draft.lastSavedAt)
      if (!Number.isNaN(parsed.getTime())) {
        return `Last saved ${formatDistanceToNow(parsed, { addSuffix: true })}`
      }
    }
    return "Autosave pending"
  })()

  const status = draft?.status ?? "draft"
  const statusIndicatorClass = (() => {
    switch (status) {
      case "submitted":
        return "bg-blue-500"
      case "under-review":
        return "bg-amber-500"
      default:
        return "bg-emerald-500"
    }
  })()

  return (
    <div className="membership-application min-h-screen bg-background text-foreground">
      <ExternalPageHeader
        isAuthenticated={auth.isAuthenticated}
        actions={
          auth.isAuthenticated ? (
            <Button asChild size="sm" variant="outline">
              <Link to="/dashboard">Back to dashboard</Link>
            </Button>
          ) : null
        }
      />
      <main className="mx-auto w-full max-w-6xl px-4 py-10">
        <div className="@container/membership grid w-full gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
          <div className="lg:sticky lg:top-24 lg:self-start">
            <ApplicationProgressSidebar
              steps={visibleSteps}
              sections={configuration?.sections}
              completedStepIds={completedStepIds}
              categoryName={category?.categoryName}
            />
          </div>
          <section className="flex flex-col gap-4">
            <header className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
              <div className="space-y-1">
                <h1 className="text-2xl font-semibold">Apply for Membership</h1>
                <p className="text-sm text-muted-foreground">
                  Complete each section below. Your progress is autosaved as you go.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 rounded-full ${statusIndicatorClass}`}
                    aria-hidden
                  />
                  {formatStatusLabel(status)}
                </span>
                <span>{autosaveLabel}</span>
              </div>
            </header>
            <div className="min-h-[320px]">
              <Outlet />
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}

function formatStatusLabel(status: string) {
  return status
    .split("-")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ")
}
